import { useEffect, useRef, useState } from 'react'
import {
  type ConsolidationEstimate,
  type ConsolidationRun,
  knowledgeConsolidationApi,
} from '../../api/knowledgeConsolidation'
import Button from '../../components/Button'
import Modal from '../../components/Modal'
import { type AreaConsolidationStatus, consolidationPhaseLabel } from './knowledgeTypes'

interface ConsolidateButtonProps {
  projectId: number
  areaId: number
  canManage: boolean
  /** In-flight run for this area (from the areas list), if any. */
  activeConsolidation?: AreaConsolidationStatus | null
  /** Called when a run this component was tracking reaches a terminal state. */
  onCompleted?: () => void
}

const TERMINAL = new Set(['succeeded', 'failed', 'budget_exceeded', 'cancelled'])

export function ConsolidateButton({
  projectId,
  areaId,
  canManage,
  activeConsolidation,
  onCompleted,
}: ConsolidateButtonProps) {
  const [estimate, setEstimate] = useState<ConsolidationEstimate | null>(null)
  const [runId, setRunId] = useState<number | null>(activeConsolidation?.runId ?? null)
  const [run, setRun] = useState<ConsolidationRun | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const onCompletedRef = useRef(onCompleted)
  onCompletedRef.current = onCompleted

  // Reset when switching areas; pick up an already-running consolidation.
  useEffect(() => {
    void areaId
    setEstimate(null)
    setRun(null)
    setOpen(false)
    setError(null)
    setRunId(activeConsolidation?.runId ?? null)
  }, [areaId, activeConsolidation?.runId])

  // Poll the run while it is in flight.
  useEffect(() => {
    if (runId === null) return
    let cancelled = false
    const tick = async () => {
      try {
        const next = await knowledgeConsolidationApi.getRun(projectId, runId)
        if (cancelled) return
        setRun(next)
        if (TERMINAL.has(next.status)) {
          setRunId(null)
          onCompletedRef.current?.()
        }
      } catch {
        // transient — keep polling
      }
    }
    void tick()
    const timer = setInterval(tick, 2500)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [projectId, runId])

  if (!canManage) return null

  const showEstimate = async () => {
    setBusy(true)
    setError(null)
    try {
      setEstimate(await knowledgeConsolidationApi.estimate(projectId, areaId))
      setOpen(true)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const start = async () => {
    setBusy(true)
    setError(null)
    try {
      const started = await knowledgeConsolidationApi.start(projectId, areaId, 'full')
      setRunId(started.runId)
      setOpen(false)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const cancel = async () => {
    if (runId === null) return
    try {
      await knowledgeConsolidationApi.cancel(projectId, runId)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    }
  }

  const inFlight = runId !== null
  const phase = run?.phase ?? activeConsolidation?.phase ?? 'queued'

  if (inFlight) {
    return (
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-300"
          aria-live="polite"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
          Consolidating · {consolidationPhaseLabel(phase)}
          {run ? ` · ${run.counters.llmCalls} LLM calls` : ''}
        </span>
        {canManage && (
          <button
            type="button"
            onClick={() => void cancel()}
            className="text-xs text-harbor-text-muted hover:text-red-400"
          >
            Cancel
          </button>
        )}
      </div>
    )
  }

  return (
    <>
      <Button onClick={showEstimate} disabled={busy}>
        Consolidate
      </Button>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
      {run && TERMINAL.has(run.status) && run.status !== 'succeeded' && (
        <p className="mt-2 text-xs text-amber-400">
          Last run {run.status.replace('_', ' ')}
          {run.errorMessage ? `: ${run.errorMessage}` : ''}
        </p>
      )}
      {run?.gaps?.length ? (
        <p className="mt-2 text-xs text-harbor-text-muted">
          {run.gaps.length} open question{run.gaps.length === 1 ? '' : 's'} raised
        </p>
      ) : null}
      <Modal isOpen={open} onClose={() => setOpen(false)} title="Knowledge consolidation estimate">
        {estimate && (
          <div className="space-y-4 text-sm text-harbor-text-secondary">
            <dl className="grid grid-cols-2 gap-2">
              <dt>Estimated LLM calls</dt>
              <dd>{estimate.llmCalls.toLocaleString()}</dd>
              <dt>Estimated tokens</dt>
              <dd>{estimate.tokens.toLocaleString()}</dd>
              <dt>Retrieval queries</dt>
              <dd>{estimate.retrievalQueries.toLocaleString()}</dd>
            </dl>
            {!estimate.withinCaps && (
              <p className="text-amber-400">This run exceeds configured limits.</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={start} disabled={busy || !estimate.withinCaps}>
                Start consolidation
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}
